/**
 * makind_workspace_as_default branch — resolves which workspace should be auto-selected as "the
 * default" for the current user, given the accessible-workspaces list AddSpaceModal.tsx already
 * fetches (GET /channels/tags). Replaces the previous `tags[0]` guess (PR #634 review fallout —
 * always picking the first result happened to work only because the backend's default config
 * scopes that endpoint to one tag per company, which isn't a resolution rule, just a coincidence
 * of today's data).
 *
 * Resolution order (spec, not negotiable — never silently fall back to a guess):
 *   1. The caller already fetched `workspaces` (the user's accessible list).
 *   2. `profileDefaultWorkspaceId` (from the user's profile), if it matches one of them.
 *   3. Otherwise, the first workspace whose `description` mentions "default workspace"
 *      (case-insensitive).
 *   4. Otherwise, null — the caller must show a clear error and stop, not pick anything.
 */
export interface WorkspaceTagLike {
  id: string;
  name: string;
  description?: string | null;
}

export function resolveDefaultWorkspace<T extends WorkspaceTagLike>(
  workspaces: T[],
  profileDefaultWorkspaceId?: string | null
): T | null {
  if (profileDefaultWorkspaceId) {
    const byProfile = workspaces.find((w) => w.id === profileDefaultWorkspaceId);
    if (byProfile) return byProfile;
  }

  const byDescription = workspaces.find((w) =>
    (w.description || '').toLowerCase().includes('default workspace')
  );
  if (byDescription) return byDescription;

  return null;
}
